import { Injectable } from '@angular/core';
import { MdrDashboardsData } from '../interfaces/mdr-dashboards-data.interface';
import { OrganizationData } from '../../organizations/interfaces/OrganizationData';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { TimeframeService } from '../../timeframe/services/timeframe.service';
import { DataResponse, IndicatorResponse } from '../interfaces/mdr-dashboards.data-response.interface';
import { OrganizationsService } from '../../organizations/services/organizations.service';
import { AuthService } from '../../auth/services/auth.service';
import { DrilldownCategory, DrilldownFilter, TableDataSource } from '../interfaces/mdr-dashboards-data-source.interface';
import { MdrDashboardsIndicatorDataInterface } from '../interfaces/mdr-dashboards-indicator-data.interface';
import { ChartAreaDataSource } from '../interfaces/mdr-dashboards-chart-area-data.interface';
import { AbstractApiV2Service } from '../../../api/services/abstract-api-v2.service';
import { FileSaverService } from '../../../api/services/file-saver.service';
import { isNumber } from 'lodash-es';
import { ToastService } from '../../../../common-modules/services/toast.service';
import { BackendErrorTranslatorService } from '../../../services/localization/backend-error-translator.service';
import { CompanyServices } from '../../auth/config/appConfig';
import { CompaniesService } from '../../companies/services/companies.service';
import { IdentityService } from '../../auth/services/identity.service';

export interface IHash {
    [index: number]: boolean;
}

export interface IFileDownloading {
    index: number;
    downloading: boolean;
}

export interface SavedAnswer {
    name: string;
    data: any;
}

@Injectable({
    providedIn: 'root',
})
export class MdrDashboardsService extends AbstractApiV2Service<MdrDashboardsData> {
    public fileIsDownloading: BehaviorSubject<IFileDownloading> = new BehaviorSubject<IFileDownloading>({
        index: -1,
        downloading: false,
    });
    public currentlyDownloading: IHash = {};
    public statusOfDeploymentValue: { currentStatus: number; total: number };
    public currentTabHasChanged = new Subject<number>();
    public savedResponses: SavedAnswer[] = [];
    public savedResponseSubject: Subject<{ name: string; saved: boolean }> = new Subject<{
        name: string;
        saved: boolean;
    }>();
    public performDetectChanges: Subject<boolean> = new Subject<boolean>();
    protected widgetDataUrl = '/v2/reporting';
    protected resourceName = 'reporting';

    public constructor(
        public organizationService: OrganizationsService,
        public authService: AuthService,
        public companiesService: CompaniesService,
        public identityService: IdentityService,
        public timeframeService: TimeframeService,
        public fileSaverService: FileSaverService,
        public toastService: ToastService,
        http: HttpClient,
        backendErrorTranslator: BackendErrorTranslatorService,
    ) {
        super(http, backendErrorTranslator);
    }

    public currentTabHasChangedObs() {
        return this.currentTabHasChanged.asObservable();
    }

    /**
     * Retrieve data from backend with specified parameters
     * @param time - selected timeframe
     * @param endpoint - name of the backend endpoint for the request
     * @param organization - selected organization
     * @param doNotAddParams -  if we want params added like company Id
     * @param saveResponse - used for when multiple widgets use the same endpoint
     * @param baseUrl - in case some widgets don take the data from the same base url
     */
    public getWidgetData(
        time: string | undefined,
        endpoint = '',
        organization: OrganizationData | null,
        saveResponse?: boolean,
        baseUrl?: string,
        doNotAddParams?: boolean,
    ): Observable<DataResponse> {
        let parent;
        if (typeof this.organizationService.currentlySelectedOrganizationHasChildren() !== 'undefined') {
            parent = this.organizationService.currentlySelectedOrganizationHasChildren() ? 1 : 0;
        } else {
            parent = 1;
        }

        let params = {
            parent: String(parent),
        };
        if (time) {
            (params as any).time = time;
        }
        if (organization) {
            (params as any).cid = String(organization.id);
        }

        return this.http
            .get<DataResponse>(
                (!baseUrl ? this.widgetDataUrl : baseUrl) + endpoint,
                doNotAddParams
                    ? {}
                    : {
                          params: params,
                      },
            )
            .pipe(
                map((response: DataResponse) => {
                    if (!response) {
                        throw new Error('Malformed response');
                    }
                    if (saveResponse) {
                        if (this.savedResponses.find((x) => x.name === endpoint)) {
                            this.savedResponses[this.savedResponses.findIndex((x) => x.name === endpoint)] = {
                                name: endpoint,
                                data: response,
                            };
                        } else {
                            this.savedResponses.push({ name: endpoint, data: response });
                        }
                        this.savedResponseSubject.next({ name: endpoint, saved: true });
                    }

                    return response;
                }),
                catchError(this.convertToUserReadableError.bind(this)),
            );
    }

    public getXdrLicensesForOverview() {
        return this.http.get('v2/licenses?filter_by_company=' + this.identityService.company?.id);
    }

    public navigateToExternalUrl(url: string | undefined) {
        window.location.href = url ?? '';
    }

    /**
     * Build filters object for each row
     * @param filters
     * @param row
     */
    public buildDrilldownFilters(filters: DrilldownFilter[] | undefined | null, row: any = {}) {
        const filterObj: { [key: string]: any } = {};

        if (typeof filters === 'undefined') {
            return [];
        }

        if (!filters) {
            return filterObj;
        }

        filters.forEach((filter: DrilldownFilter) => {
            let item = filter.item;
            if (typeof item === 'object' && !Array.isArray(item)) {
                filterObj[item.nativeName] = item.customValue ? item.customValue : row[item.apiName];
            } else {
                if (item === 'timeframe') {
                    filterObj[item as string] = this.timeframeService.currentlySelectedTimeframe()?.name;
                } else {
                    filterObj[item as string] = row[item as string];
                }
            }
        });

        return filterObj;
    }

    /**
     * Build drilldown router links for each row
     * @param target - where to redirect from drilldown
     * @param row - each row of the table
     */
    public buildDrilldownRouterLink(target: string | DrilldownCategory[] | undefined, row: any | IndicatorResponse | null = {}) {
        if (typeof target === 'undefined' || !target || row.category === '...') {
            return [];
        }

        if (Array.isArray(target) && row.category) {
            let obj = target.filter((o: DrilldownCategory) => {
                return o.category === row.category;
            });

            return obj[0] ? [obj[0].url] : [];
        }

        return [target];
    }

    public intToString(value: number | undefined, min = 1000, approximate = false, digits = 1) {
        if (typeof value === 'undefined') {
            return '';
        }
        if (value == null && value !== 0) {
            return '- -';
        }

        if (value >= min && approximate) {
            const lookup = [
                { value: 1, symbol: '' },
                { value: 1e3, symbol: 'K' },
                { value: 1e6, symbol: 'M' },
                { value: 1e9, symbol: 'G' },
                { value: 1e12, symbol: 'T' },
                { value: 1e15, symbol: 'P' },
                { value: 1e18, symbol: 'E' },
            ];
            const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
            const item = lookup
                .slice()
                .reverse()
                .find(function (item) {
                    return value >= item.value;
                });

            return item ? (value / item.value).toFixed(digits).replace(rx, '$1') + item.symbol : '0';
        } else {
            return value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        }
    }

    /**
     * Check if thw widget can be hoverable and has drilldown
     * @param element - HTML element to be hoverable
     * @param row - row of the table
     * @param indexForDownload
     */
    public isHoverable(
        element: TableDataSource | MdrDashboardsIndicatorDataInterface | ChartAreaDataSource,
        row: any,
        indexForDownload?: number,
    ): boolean | undefined {
        if (isNumber(indexForDownload)) {
            return element?.drilldown?.show && row[Object.keys(row)[0]] !== '...' && !this.currentlyDownloading[indexForDownload];
        }
        return element?.drilldown?.show && row[Object.keys(row)[0]] !== '...';
    }

    /**
     * Limit a variable's value to a range between a min and a max value.
     * @param value
     * @param min
     * @param max
     */
    public limitValueToMinMax(value: number, min: number, max: number) {
        return value > min ? (value < max ? value : max) : min;
    }

    public isLcoCompany(organization: OrganizationData | null = this.identityService.company) {
        if (!organization) {
            organization = this.identityService.company;
        }

        const companyToCheck = this.organizationService.isAllManagedCompaniesSelected ? this.identityService.company : organization;

        return (
            companyToCheck.details?.configuration?.service_level === CompanyServices.Managed_Endpoint_Detection_and_Response ||
            companyToCheck.details?.configuration?.service_level === CompanyServices.MDR_Core ||
            companyToCheck.details?.configuration?.service_level === CompanyServices.MDR_Foundations_for_MSP ||
            companyToCheck.details?.configuration?.service_level === CompanyServices.MDR_Foundations ||
            companyToCheck.details?.configuration?.service_level === CompanyServices.Bitdefender_MDR
        );
    }

    public isPremiumEnterprise(organization: OrganizationData | null = this.identityService.company) {
        if (!organization) {
            organization = this.identityService.company;
        }

        const companyToCheck = this.organizationService.isAllManagedCompaniesSelected ? this.identityService.company : organization;

        return (
            companyToCheck.details?.configuration?.service_level === CompanyServices.MDR_Enterprise ||
            companyToCheck.details?.configuration?.service_level === CompanyServices.MDR_Advanced ||
            companyToCheck.details?.configuration?.service_level === CompanyServices.Bitdefender_MDR_PLUS
        );
    }

    /**
     * Function to download specified file from server
     */
    public downloadDocumentObs(endpoint: string, filename: string, type: string, index: number): Observable<void> {
        this.fileIsDownloading.next({ index, downloading: true });
        this.currentlyDownloading[index] = true;
        return this.fileSaverService.saveFile(endpoint, filename, type).pipe(
            map((value) => {
                this.fileIsDownloading.next({ index, downloading: false });
                this.fileIsDownloading.complete();
                this.currentlyDownloading[index] = false;
            }),
            catchError((err) => {
                this.fileIsDownloading.next({ index, downloading: false });
                this.fileIsDownloading.complete();
                this.toastService.showError(' Something went wrong. ', {
                    duration: 3000,
                });
                return this.convertToUserReadableError(err);
            }),
        );
    }

    public fileIsDownloadingObs(): Observable<{
        index: number;
        downloading: boolean;
    }> {
        return this.fileIsDownloading.asObservable();
    }

    protected getLocation(resourceId?: string): string {
        if (resourceId) {
            return `/v2/reporting/${this.resourceName}/${resourceId}`;
        }

        return `/v2/reporting/${this.resourceName}`;
    }
}
